/**
 * The signed-in user's own preferences, as the rest of the app reads them.
 *
 * Stored server-side on the user's continuum.usergroup.extra_data and read/written
 * through auth.GetPreferencesForSelf / auth.UpdatePreferencesForSelf
 * (preferences-rpc-client.ts), so a choice follows the user to every browser.
 * This module is the in-memory copy and nothing else — no RPC imports — so
 * synchronous readers (postLoginRedirect, nav-items) can use it without
 * pulling the hermes client in. FEATURE-20260915-user-profile-page-and-preferences.
 */
import { useSyncExternalStore } from "react";

export interface UserPreferences {
  /** in-app path to land on after login; unset = the customer profile's home */
  loginRedirectUrl?: string;
  /** in-app paths pinned to the top of the command palette */
  favoritePaths?: string[];
  /** per-user override of the build's app.showDemoPages; unset = follow the build */
  showDemoPages?: boolean;
}

let current: UserPreferences = {};
const listeners = new Set<() => void>();

export function getPreferences(): UserPreferences {
  return current;
}

export function setPreferences(prefs: UserPreferences): void {
  current = prefs;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePreferences(): UserPreferences {
  return useSyncExternalStore(subscribe, getPreferences);
}

/**
 * A path inside this app: one leading "/", nothing a browser resolves to
 * another origin. The server enforces the same rule (auth-service
 * validInAppPath); this copy only keeps the UI from offering what the server
 * would refuse.
 */
export function isInAppPath(p: string): boolean {
  return p.startsWith("/") && !p.startsWith("//") && !/[\\\r\n\t]/.test(p);
}
